import React, { useState, useEffect, useMemo } from "react";

import ReactApexChart from "react-apexcharts";
import useChartColors from "Components/Common/useChartColors";
import { getPaymentsPerCategory } from "common/services/payments.service";
import useQueryState from "Components/Hooks/UseQueryState";
import { getYearDateRange } from "common/lib/dateUtils";

const groupData = (payments: any) => {
  const totalCounts = payments.map((payment: { totalcount: any }) =>
    Number(payment.totalcount)
  );
  const categories = payments.map(
    (payment: { category: any }) => payment.category
  );

  return { totalCounts, categories };
};

const ContractDistributionChart = ({ series, labels }: any) => {
  const chartId = "portfolio_donut_charts";
  const chartColors = useChartColors(chartId);

  const options: any = {
    labels,
    chart: {
      type: "donut",
      height: 250,
    },

    plotOptions: {
      pie: {
        size: 90,
        offsetX: 0,
        offsetY: 0,
        donut: {
          size: "70%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "18px",
              offsetY: -5,
            },
            value: {
              show: true,
              fontSize: "20px",
              color: "#343a40",
              fontWeight: 500,
              offsetY: 5,
              formatter: function (val: any) {
                return val.toLocaleString();
              },
            },
            total: {
              show: true,
              fontSize: "13px",
              label: "Total",
              color: "#9599ad",
              fontWeight: 500,
              formatter: function (w: any) {
                const total = w.globals.seriesTotals.reduce(function (
                  a: any,
                  b: any
                ) {
                  return a + b;
                },
                0);
                return total.toLocaleString();
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
      formatter: function (val: any) {
        return val.toLocaleString();
      },
    },
    legend: {
      show: false,
    },
    yaxis: {
      labels: {
        formatter: function (value: any) {
          return value.toLocaleString();
        },
      },
    },
    stroke: {
      lineCap: "round",
      width: 2,
    },
    colors: chartColors,
  };

  return (
    <React.Fragment>
      <ReactApexChart
        dir="ltr"
        options={options}
        series={series}
        id={chartId}
        type="donut"
        height="250"
        data-colors='["#088F7D", "--vz-success", "--vz-warning", "--vz-danger", "--vz-secondary"]'
        className="apex-charts"
      />
    </React.Fragment>
  );
};

const PaymentCategory = () => {
  const [payments, setPayments] = useState<[]>([]);
  const [, setLoading] = useState(false);

  const [selectedYear] = useQueryState(
    "year",
    new Date().getFullYear().toString()
  );

  const { firstDay, lastDay } = getYearDateRange(selectedYear);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getPaymentsPerCategory({
          startDate: firstDay ? firstDay : undefined,
          endDate: lastDay ? lastDay : undefined,
        });

        setPayments(response);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [firstDay, lastDay]);

  const { totalCounts, categories } = useMemo(
    () => groupData(payments),
    [payments]
  );

  const dataColors = useChartColors("portfolio_donut_charts");

  return (
    <React.Fragment>
      <div className="card card-height-100">
        <div className="card-header  align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">
            Distribution of Support by Category
          </h4>
        </div>
        <div className="card-body">
          <ContractDistributionChart series={totalCounts} labels={categories} />

          {categories.map((category: any, index: number) => (
            <div
              key={index}
              className="d-flex justify-content-between border-bottom border-bottom-dashed py-2"
            >
              <p className="fw-medium mb-0">
                <i
                  style={{
                    color: dataColors[index],
                  }}
                  className={`ri-checkbox-blank-circle-fill align-middle me-2`}
                ></i>{" "}
                {category}
              </p>
              <div>
                <span className="text-muted pe-5">
                  {totalCounts[index].toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </React.Fragment>
  );
};

export default PaymentCategory;
